import { createSignal, createResource, getOwner, onCleanup, Show, onMount, createEffect, on } from 'solid-js';
import { Select } from '@thisbeyond/solid-select';
import { twclsx, apiFetch, Icons } from '../shared';
import { Tax } from '../globals';

export type Master = {
  id: string;
  name: string;
  measurement_unit_symbol?: string;
  price?: number;
  taxes?: Tax[];
  description?: string;
};

export type MasterType = 'products' | 'accounts' | 'parties';
export type MasterSelectInitialValue = Master | string | undefined | null;

type MasterSelectProps = {
  id: string;
  label?: string;
  name: string;
  entityId: string;
  class?: string;
  selectClass?: string;
  placeholder?: string;
  types: MasterType[];
  cacheTimeout?: number;
  initialValue?: MasterSelectInitialValue;
  format: (master: Master) => string;
  onChange: (master: Master) => void;
  createNew?: () => void;
};

export const [refreshOptions, setMasterSelectRefreshOptions] = createSignal(undefined, { equals: false });

// eslint-disable-next-line comma-spacing, @typescript-eslint/no-explicit-any
const throttle = <T,>(callback: (...args: any[]) => Promise<T>, threshold: number) => {
  let activePromise: Promise<T> | null = null;
  let timeoutId: ReturnType<typeof setTimeout>;
  let lastArgs: Parameters<typeof callback>;

  const wait = () =>
    new Promise(resolve => {
      timeoutId = setTimeout(resolve, threshold);
    });

  const throttled: typeof callback = (...args) => {
    lastArgs = args;
    if (activePromise) {
      return activePromise;
    }

    activePromise = wait().then(() => {
      activePromise = null;
      return callback(...lastArgs);
    });

    return activePromise;
  };

  const clear = () => {
    clearTimeout(timeoutId);
    activePromise = null;
  };

  if (getOwner()) {
    onCleanup(clear);
  }

  return Object.assign(throttled, { clear });
};

type MasterTaxes = {
  master: { name: string; id: string };
  taxes: Tax[];
};

const filterSort = (data: Master[]) => {
  const seen = new Set();

  return data
    .filter(({ id }) => {
      if (seen.has(id)) {
        return false;
      }

      seen.add(id);
      return true;
    })
    .sort((a, b) => a.name.localeCompare(b.name));
};

// TODO: provide interface to invalidate cache
const CACHE_TIMEOUT = 1000 * 60 * 5;
// const cache: { [key: string]: { data: Promise<Master[]>; expires: number } } = {};
const cache = new Map<string, { data: Promise<Master[]>; expires: number }>();

const getOptions = async ([entityId, types, name, cacheTimeout]: [string, MasterType[], string, number]): Promise<
  Master[]
> => {
  const cacheKey = `${entityId}-${types.join('-')}-${name}`;
  const currentTime = Date.now();

  if (cache.has(cacheKey) && currentTime < cache.get(cacheKey)!.expires) {
    return filterSort(await cache.get(cacheKey)!.data);
  }

  const results = Promise.all(
    types.map(async type => {
      // TODO: handle errorr
      const products = await (await apiFetch(`/entities/${entityId}/${type}?${new URLSearchParams({ name })}`)).json();
      if (Array.isArray(products)) {
        return (products as Array<MasterTaxes>).map(({ master, taxes }) => ({ ...master, taxes }));
      }

      return [];
    }),
  ).then(data => data.flat());

  cache.set(cacheKey, {
    data: results,
    expires: currentTime + cacheTimeout,
  });

  return filterSort(await cache.get(cacheKey)!.data);
};

const MasterSelect = (props: MasterSelectProps, timeout = 500) => {
  const [inputValue, setInputValue] = createSignal('');
  const [initialValue, setInitialValue] = createSignal<MasterSelectInitialValue>(null);

  const throttledFetcher = throttle(getOptions, timeout);

  const [asyncOptions, { refetch }] = createResource<Master[], [string, MasterType[], string, number]>(
    () => [props.entityId, props.types, inputValue(), props.cacheTimeout || CACHE_TIMEOUT] as const,
    throttledFetcher,
    {
      initialValue: [],
    },
  );

  onMount(async () => {
    const options = await getOptions([props.entityId, props.types, '', props.cacheTimeout || CACHE_TIMEOUT]);

    if (typeof props.initialValue === 'string' && options && options.length) {
      setInitialValue(options.find((option: Master) => option.id === props.initialValue));
    }
  });

  createEffect(() => {
    if (props.initialValue) {
      if (typeof props.initialValue === 'string') {
        setInitialValue(
          props.initialValue === '' ? null : asyncOptions()?.find((option: Master) => option.id === props.initialValue),
        );
      } else {
        setInitialValue(Object.keys(props.initialValue).length ? props.initialValue : null);
      }
    } else {
      setInitialValue(null);
    }
  });

  createEffect(
    on(
      refreshOptions,
      () => {
        cache.delete(`${props.entityId}-${props.types.join('-')}-`);
        refetch();
      },
      { defer: true },
    ),
  );

  return (
    <div class={props.class}>
      <Show when={props.label}>
        <label
          for={props.id}
          class={twclsx('block', 'mb-2', 'text-sm', 'font-medium', 'text-gray-900', 'dark:text-white')}
        >
          {props.label}
        </label>
      </Show>
      <div class="flex items-center">
        <Select
          format={props.format}
          placeholder={asyncOptions.loading ? 'Loading...' : props.placeholder}
          class={twclsx(
            'block',
            'w-full',
            'p-2.5',
            'border',
            'text-sm',
            props.createNew ? 'rounded-s-lg' : 'rounded-lg',
            'bg-gray-50',
            'border-gray-300',
            'text-gray-900',
            'focus:ring-blue-500',
            'focus:border-blue-500',
            'dark:bg-gray-700',
            'dark:border-gray-600',
            'dark:placeholder-gray-400',
            'dark:text-white',
            'dark:focus:ring-blue-500',
            'dark:focus:border-blue-500',
            props.selectClass,
          )}
          onChange={props.onChange}
          initialValue={initialValue()}
          onInput={setInputValue}
          options={asyncOptions()}
          loading={asyncOptions.loading}
          readonly={false}
        />
        <Show when={props.createNew}>
          <button
            type="button"
            class={twclsx(
              'inline-flex',
              'items-center',
              'justify-center',
              'p-1',
              'text-sm',
              'font-medium',
              'text-center',
              'text-white',
              'rounded-e-lg',
              'bg-primary-700',
              'hover:bg-primary-800',
              'focus:ring-4',
              'focus:ring-primary-300',
              'dark:bg-primary-600',
              'dark:hover:bg-primary-700',
              'dark:focus:ring-primary-800',
            )}
            onClick={() => {
              props.createNew?.();
            }}
          >
            <Icons.Plus class={twclsx('w-8', 'h-8', 'text-white')} />
          </button>
        </Show>
      </div>
    </div>
  );
};

export default MasterSelect;
